<template>
  <client-only>
    <div class="wishlist-widget">
      <a
        href="#"
        class="wishlist-btn"
        :class="{
          'wishlist-btn--added': onWishlist,
          'wishlist-btn--requesting': requesting,
        }"
        rel="nofollow"
        @click.prevent="updateWishlist"
      >
        <span class="wishlist-btn__heart">
          <div v-if="!onWishlist">
            <heart-icon color="#555" css-class="wishlist-btn__heart-icon" />
          </div>
          <div v-else>
            <heart-filled-icon
              class="wishlist-btn__heart-icon wishlist-btn__heart-icon--filled"
            />
          </div>
          <div v-if="showAnimation" class="whishlist-btn__animation"></div>
        </span>
      </a>
      <modal
        data-modal="wishlist-widget"
        :open="isModalOpen"
        @close="closeLoginModal"
      >
        <template v-slot:title>
          {{ $t('wishlist.modal_title') }}
        </template>
        <v-content class="login-page" size="thin">
          <auth-box
            v-if="isModalOpen"
            :redirect-on-login-success="false"
            @success="loginSuccess"
          />
        </v-content>
      </modal>
    </div>
  </client-only>
</template>

<script>
import { mapGetters } from 'vuex'
import isPresent from '@/utils/isPresent'
import appendUrlParam from '~/utils/appendUrlParam'
import HeartIcon from '~/components/icons/heart/Heart'
import HeartFilledIcon from '~/components/icons/heart-filled/HeartFilledIcon'
import Modal from '~/components/Modal'
import VContent from '~/components/shared/v-content/VContent'
import pushDataLayer from '~/mixins/gtm/push-data-layer'
import Ga4Parser from '~/utils/Ga4Parser'

const AuthBox = () =>
  import(/* webpackChunkName: "auth-box"  */ '~/components/auth-box/AuthBox')

export default {
  name: 'WishlistWidget',
  components: {
    HeartIcon,
    HeartFilledIcon,
    Modal,
    AuthBox,
    VContent,
  },
  mixins: [pushDataLayer],
  data() {
    return {
      currentProduct: this.$store.state?.product?.product,
      onWishlist: false,
      isModalOpen: false,
      requesting: false,
      showAnimation: false,
    }
  },
  computed: {
    ...mapGetters(['storeInfo']),
    isLogged() {
      return this.$store.getters.isLogged
    },
    productToGtm() {
      return {
        id: this.currentProduct?.id,
        name: this.currentProduct?.name,
        sku: this.currentProduct?.sku,
        // eslint-disable-next-line camelcase
        category: this.currentProduct?.product_type_code,
        user_id: this.$store.state?.user?.id,
        price:
          this.$store.state?.product?.currentPrice ||
          // eslint-disable-next-line camelcase
          this.currentProduct?.price_with_cost,
      }
    },
  },
  watch: {
    onWishlist(newVal, oldVal) {
      if (newVal && !oldVal) this.showAnimation = true
    },
  },
  async mounted() {
    if (this.isLogged && isPresent(this.currentProduct)) {
      const params = {
        ...this.storeInfo,
        product_id: this.currentProduct?.id,
      }

      const { presence } = await this.$apiv2.$get(`wishlist/product_presence`, {
        params,
      })

      this.onWishlist = presence
    }
  },
  methods: {
    sendToGtm() {
      const prod = Object.assign(
        JSON.parse(JSON.stringify(this.currentProduct)),
        {
          product_id: this.currentProduct?.id,
          price:
            this.$store.state?.product?.currentPrice ||
            // eslint-disable-next-line camelcase
            this.currentProduct?.price_with_cost,
          promotion:
            this.$store.state?.product?.currentPromotion ||
            this.currentProduct.promotion,
          // eslint-disable-next-line camelcase
          case_device_id: this.currentProduct?.default_case_device_id,
        }
      )

      const ga4Items = new Ga4Parser(this.$store.state).addToWishlist(prod)

      const ecommerce = new Ga4Parser(this.$store.state).addToWishlistV2(prod)

      /* 
        Prevent Side Effect since ga4EventValue is an array, 
        gtm automaticly append a new item instead of create a new array  
      */
      this.pushDataLayer({
        ga4EventValue: undefined,
      })

      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'interactWithPage',
        eventAction: 'clickOnButton',
        eventLabel: 'addToWishlist',
        eventValue: this.productToGtm,
        ecommerce,
        ga4EventValue: ga4Items,
      })
    },
    loginSuccess() {
      this.closeLoginModal()
      this.updateWishlist()
    },
    closeLoginModal() {
      this.isModalOpen = false
    },
    updateWishlist() {
      if (this.requesting) return

      if (!this.isLogged) {
        this.isModalOpen = true
        return
      }

      if (this.onWishlist) this.removeFromWhishlist()
      else this.addOnWishlist()
    },
    async addOnWishlist() {
      const params = {
        ...this.storeInfo,
        product_id: this.currentProduct?.id,
      }

      this.requesting = true

      try {
        await this.$apiv2.$post(appendUrlParam('wishlist/add_product', params))
        this.onWishlist = true
        this.sendToGtm()
      } catch (err) {
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      } finally {
        this.requesting = false
      }
    },
    async removeFromWhishlist() {
      const params = {
        ...this.storeInfo,
        product_id: this.currentProduct?.id,
      }

      this.requesting = true

      try {
        await this.$apiv2.$delete('wishlist/remove_product', {
          params,
        })
        this.onWishlist = false
      } catch (err) {
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      } finally {
        this.requesting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wishlist-widget {
  display: flex;
  align-items: center;
  margin-left: 20px;
}

.wishlist-btn {
  position: relative;
  color: #555;
  text-decoration: none;
  margin: 0;
  padding: 2px 0 0 1px;
  width: 50px;
  height: 50px;
  box-sizing: border-box;
  background: $color_gray;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background-color: #e2e2e6;
  }

  .wishlist-btn__heart {
    vertical-align: bottom;
  }

  .wishlist-btn__text {
    vertical-align: text-bottom;
  }
}

.wishlist-btn--requesting {
  opacity: 0.5;
}

.wishlist-btn__heart {
  position: relative;
  display: inline-block;
  line-height: 1;
  vertical-align: bottom;

  .wishlist-btn__heart-icon {
    width: 24px;
    height: 24px;
    margin-top: 3px;
    margin-left: 4px;
  }

  .wishlist-btn__heart-icon--filled {
    width: 20px;
    height: 20px;
    margin: 0;
  }
}

.wishlist-btn--added {
  .whishlist-btn__animation {
    margin-top: 2.5px;
    margin-left: 2px;
    color: #e2264d;
    -webkit-filter: none;
    filter: none;
    will-change: font-size;
    -webkit-animation: heart 1s cubic-bezier(0.17, 0.89, 0.32, 1.49);
    animation: heart 1s cubic-bezier(0.17, 0.89, 0.32, 1.49);
  }
  .whishlist-btn__animation:before,
  .whishlist-btn__animation:after {
    -webkit-animation: inherit;
    animation: inherit;
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }
  .whishlist-btn__animation:before {
    will-change: transform, border-width, border-color;
    -webkit-animation-name: bubble;
    animation-name: bubble;
  }

  .whishlist-btn__animation:after {
    will-change: opacity, box-shadow;
    -webkit-animation-name: sparkles;
    animation-name: sparkles;
  }
}

.whishlist-btn__animation {
  align-self: center;
  position: absolute;
  top: 0;
  left: 0;
  color: #888;
  font-size: 2em;
  -webkit-filter: grayscale(1);
  filter: grayscale(1);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  cursor: pointer;
}
.whishlist-btn__animation:before,
.whishlist-btn__animation:after {
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  border-radius: 50%;
  content: '';
}

.whishlist-btn__animation:before {
  box-sizing: border-box;
  margin: -27px;
  border: solid 2.25rem #e2264d;
  width: 4.5rem;
  height: 4.5rem;
  -webkit-transform: scale(0);
  transform: scale(0);
}

.whishlist-btn__animation:after {
  margin: 0.575rem;
  width: 0.375rem;
  height: 0.375rem;
  box-shadow: 0.32476rem -3rem 0 -0.1875rem #ff8080,
    -0.32476rem -2.625rem 0 -0.1875rem #ffed80,
    2.54798rem -1.61656rem 0 -0.1875rem #ffed80,
    1.84982rem -1.89057rem 0 -0.1875rem #a4ff80,
    2.85252rem 0.98418rem 0 -0.1875rem #a4ff80,
    2.63145rem 0.2675rem 0 -0.1875rem #80ffc8,
    1.00905rem 2.84381rem 0 -0.1875rem #80ffc8,
    1.43154rem 2.22414rem 0 -0.1875rem #80c8ff,
    -1.59425rem 2.562rem 0 -0.1875rem #80c8ff,
    -0.84635rem 2.50595rem 0 -0.1875rem #a480ff,
    -2.99705rem 0.35095rem 0 -0.1875rem #a480ff,
    -2.48692rem 0.90073rem 0 -0.1875rem #ff80ed,
    -2.14301rem -2.12438rem 0 -0.1875rem #ff80ed,
    -2.25479rem -1.38275rem 0 -0.1875rem #ff8080;
}

@-webkit-keyframes heart {
  0%,
  17.5% {
    font-size: 0;
  }
}

@keyframes heart {
  0%,
  17.5% {
    font-size: 0;
  }
}
@-webkit-keyframes bubble {
  15% {
    -webkit-transform: scale(1);
    transform: scale(1);
    border-color: #cc8ef5;
    border-width: 2.25rem;
  }
  30%,
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
    border-color: #cc8ef5;
    border-width: 0;
  }
}
@keyframes bubble {
  15% {
    -webkit-transform: scale(1);
    transform: scale(1);
    border-color: #cc8ef5;
    border-width: 2.25rem;
  }
  30%,
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
    border-color: #cc8ef5;
    border-width: 0;
  }
}
@-webkit-keyframes sparkles {
  0%,
  20% {
    opacity: 0;
  }
  25% {
    opacity: 1;
    box-shadow: 0.32476rem -2.4375rem 0 0rem #ff8080,
      -0.32476rem -2.0625rem 0 0rem #ffed80,
      2.1082rem -1.26585rem 0 0rem #ffed80,
      1.41004rem -1.53985rem 0 0rem #a4ff80,
      2.30412rem 0.85901rem 0 0rem #a4ff80, 2.08305rem 0.14233rem 0 0rem #80ffc8,
      0.76499rem 2.33702rem 0 0rem #80ffc8, 1.18748rem 1.71734rem 0 0rem #80c8ff,
      -1.35019rem 2.0552rem 0 0rem #80c8ff,
      -0.60229rem 1.99916rem 0 0rem #a480ff,
      -2.44865rem 0.22578rem 0 0rem #a480ff,
      -1.93852rem 0.77557rem 0 0rem #ff80ed,
      -1.70323rem -1.77366rem 0 0rem #ff80ed,
      -1.81501rem -1.03204rem 0 0rem #ff8080;
  }
}
@keyframes sparkles {
  0%,
  20% {
    opacity: 0;
  }
  25% {
    opacity: 1;
    box-shadow: 0.32476rem -2.4375rem 0 0rem #ff8080,
      -0.32476rem -2.0625rem 0 0rem #ffed80,
      2.1082rem -1.26585rem 0 0rem #ffed80,
      1.41004rem -1.53985rem 0 0rem #a4ff80,
      2.30412rem 0.85901rem 0 0rem #a4ff80, 2.08305rem 0.14233rem 0 0rem #80ffc8,
      0.76499rem 2.33702rem 0 0rem #80ffc8, 1.18748rem 1.71734rem 0 0rem #80c8ff,
      -1.35019rem 2.0552rem 0 0rem #80c8ff,
      -0.60229rem 1.99916rem 0 0rem #a480ff,
      -2.44865rem 0.22578rem 0 0rem #a480ff,
      -1.93852rem 0.77557rem 0 0rem #ff80ed,
      -1.70323rem -1.77366rem 0 0rem #ff80ed,
      -1.81501rem -1.03204rem 0 0rem #ff8080;
  }
}
</style>
